import { SearchCheck } from "lucide-react";
import type { SeoMetadata } from "../types/api";
import { Input, Switch, Textarea } from "./ui";

export type SeoEditorErrors = Partial<Record<keyof SeoMetadata, string>>;

export function SeoEditor({
  value,
  onChange,
  errors,
  intro = "متادیتا را دقیق، منحصربه‌فرد و متناسب با محتوای واقعی همین صفحه تنظیم کنید.",
  titleHint = "اگر خالی بماند، عنوان اصلی صفحه به‌عنوان مقدار پیش‌فرض استفاده می‌شود.",
  descriptionHint = "خلاصه‌ای روشن و کاربردی بنویسید؛ از تکرار مصنوعی کلیدواژه پرهیز کنید.",
  imageHint = "URL تصویر مناسب برای متادیتا و اشتراک‌گذاری؛ در صورت خالی بودن می‌توان از تصویر اصلی صفحه استفاده کرد.",
  canonicalPlaceholder = "https://example.com/...",
  indexHint = "در حالت عادی روشن باشد؛ فقط برای صفحه‌ای که نباید ایندکس شود خاموش کنید.",
  followHint = "در حالت عادی روشن باشد تا لینک‌های صفحه توسط خزنده‌ها دنبال شوند.",
}: {
  value: SeoMetadata;
  onChange: (value: SeoMetadata) => void;
  errors?: SeoEditorErrors;
  intro?: string;
  titleHint?: string;
  descriptionHint?: string;
  imageHint?: string;
  canonicalPlaceholder?: string;
  indexHint?: string;
  followHint?: string;
}) {
  const patch = (next: Partial<SeoMetadata>) => onChange({ ...value, ...next });
  const descriptionLength = (value.description ?? "").length.toLocaleString("fa-IR");

  return (
    <div className="seo-editor">
      <div className="seo-intro mb-5">
        <SearchCheck />
        <div>
          <strong>تنظیمات نمایش در جست‌وجو</strong>
          <p>{intro}</p>
        </div>
      </div>
      <div className="form-grid">
        <Input
          label="عنوان SEO"
          maxLength={200}
          value={value.title ?? ""}
          error={errors?.title}
          hint={titleHint}
          onChange={(event) => patch({ title: event.target.value })}
        />
        <Input
          label="Canonical URL"
          dir="ltr"
          placeholder={canonicalPlaceholder}
          value={value.canonicalUrl ?? ""}
          error={errors?.canonicalUrl}
          hint="اختیاری؛ فقط URL کامل نسخه ترجیحی صفحه را وارد کنید."
          onChange={(event) => patch({ canonicalUrl: event.target.value })}
        />
        <Textarea
          className="span-2"
          label="Meta Description"
          rows={4}
          maxLength={500}
          value={value.description ?? ""}
          error={errors?.description}
          hint={`${descriptionLength} نویسه · ${descriptionHint}`}
          onChange={(event) => patch({ description: event.target.value })}
        />
        <Input
          label="تصویر SEO"
          dir="ltr"
          value={value.imageUrl ?? ""}
          error={errors?.imageUrl}
          hint={imageHint}
          onChange={(event) => patch({ imageUrl: event.target.value })}
        />
        <div className="switch-stack">
          <Switch label="اجازه Index" checked={value.index} hint={indexHint} onChange={(checked) => patch({ index: checked })} />
          <Switch label="اجازه Follow" checked={value.follow} hint={followHint} onChange={(checked) => patch({ follow: checked })} />
        </div>
      </div>
    </div>
  );
}
